import { Box, Button, Typography, TextField, Divider, Grid } from "@mui/material";
import { RefreshCalculator } from "../controllers/calculation";
import { NumberWithCommas } from "../controllers/utils";
import { useEffect, useState } from "react";

// COMPONENTS
import Footer from "../components/Footer";
import Navbar from "../components/Navbar";
import CopyCommand from "../components/CopyCommand";
import Charge from "../components/Charge";
import FeedbackAlert from "../components/FeedbackAlert";

// CALCULATOR
export default function Calculator({ chargesCalculator, setChargesCalculator, violationCalculator, setViolationCalculator }: CalculatorProps) {
    const [totalFine, setTotalFine] = useState(0);
    const [totalTime, setTotalTime] = useState(0);
    const [totalPoints, setTotalPoints] = useState(0);

    // CALCULATOR DATA STATES
    const [targetSSN, setTargetSSN] = useState(localStorage.getItem('targetSSN') ?? 0);
    const [targetServerID, setTargetServerID] = useState(localStorage.getItem('targetServerID') ?? 0);
    const [chargesString, setChargesString] = useState('');

    // NOTIFICATION STATES
    const [showNotification, setShowNotification] = useState(false);
    const [notificationText, setNotificationText] = useState('');

    // RESET CALCULATOR FUNCTION
    const ResetCalculator = () => {
        setChargesCalculator([]);
        setViolationCalculator([]);
    }

    // ANY CHANGES TO THE CHARGES OR VIOLATIONS WILL REFRESH THE CALCULATIONS
    useEffect(() => {
        RefreshCalculator(chargesCalculator, violationCalculator, setTotalFine, setTotalTime, setTotalPoints, setChargesString)
    }, [chargesCalculator, violationCalculator]);

    // RETURN ELEMENT
    return (
        <>
            {/* NOTIFICATION */}
            <FeedbackAlert
                show={showNotification}
                timeout={6000}
                message={notificationText}
                onClose={() => setShowNotification(false)}
            />

            {/* BODY */}
            <Box sx={{ height: '100vh', overflowX: 'hidden', overflowY: 'scroll' }}>
                <Navbar />

                {/* MAIN BODY */}
                <Box component="main" sx={{ minHeight: '100vh', px: 5, pb: 2 }}>
                    <Box 
                        sx={{
                            display: 'flex',
                            justifyContent: { xs: 'normal', md: 'space-between' },
                            flexDirection: { xs: 'column', md: 'row' },
                            mt: 3,
                            p: 3,
                            bgcolor: 'var(--darkbackground)',
                            borderRadius: 2
                        }}
                    >
                        <Box>
                            <Typography variant="h5" fontFamily="Public Sans" color="white" gutterBottom>
                                Total Fine: <span style={{ color: 'red' }}>${NumberWithCommas(totalFine)}</span>
                            </Typography>

                            <Typography variant="h5" fontFamily="Public Sans" color="white" gutterBottom>
                                Total Time: <span style={{ color: 'red' }}>{NumberWithCommas(totalTime)}</span>
                            </Typography>

                            <Typography variant="h5" fontFamily="Public Sans" color="white">
                                Total Points: <span style={{ color: 'red' }}>{NumberWithCommas(totalPoints)}</span>
                            </Typography>

                            <Button
                                color="error"
                                variant="outlined"
                                aria-label="Clear"
                                sx={{ fontSize: 20, fontFamily: 'Public Sans', mt: 4 }}
                                onClick={() => ResetCalculator()}
                            >
                                Clear
                            </Button>
                        </Box>

                        <Divider sx={{ display: { xs: 'block', md: 'none' }, my: 3.5 }} />

                        <Box>
                            <Box>
                                <TextField
                                    type="number"
                                    label="Target SSN"
                                    size="small"
                                    fullWidth
                                    value={targetSSN}
                                    onInput={(e: any) => {
                                        setTargetSSN(e.target.value);
                                        localStorage.setItem('targetSSN', e.target.value);
                                    }}
                                />
                            </Box>
                            
                            <Box>
                                <TextField
                                    type="number"
                                    label="Target Server ID"
                                    size="small"
                                    margin="normal"
                                    fullWidth
                                    value={targetServerID}
                                    onInput={(e: any) => {
                                        setTargetServerID(e.target.value);
                                        localStorage.setItem('targetServerID', e.target.value);
                                    }}
                                />
                            </Box>

                            <Box>
                                <TextField
                                    sx={{ width: { md: 600 } }}
                                    value={`/ticket ${targetSSN} ${totalFine} ${chargesString}`}
                                    size="small"
                                    margin="normal"
                                    fullWidth
                                    disabled
                                    InputProps={{
                                        startAdornment: <CopyCommand commandString={`/ticket ${targetSSN} ${totalFine} ${chargesString}`} />
                                    }}
                                />
                            </Box>

                            <Box>
                                <TextField
                                    sx={{ width: { md: 600 } }}
                                    value={`/bill ${targetSSN} ${totalFine} ${chargesString}`}
                                    size="small"
                                    margin="normal"
                                    fullWidth
                                    disabled
                                    InputProps={{
                                        startAdornment: <CopyCommand commandString={`/bill ${targetSSN} ${totalFine} ${chargesString}`} />
                                    }}
                                />
                            </Box>

                            <Box>
                                <TextField
                                    sx={{ width: { md: 600 } }}
                                    value={`/jail ${targetServerID} ${totalTime}`}
                                    size="small"
                                    margin="normal"
                                    fullWidth
                                    disabled
                                    InputProps={{
                                        startAdornment: <CopyCommand commandString={`/jail ${targetServerID} ${totalTime}`} />
                                    }}
                                />
                            </Box>
                        </Box>
                    </Box>

                    <Box sx={{ mt: 3, p: 3, bgcolor: 'var(--darkbackground)', borderRadius: 2}}>
                        <Grid container spacing={3}>
                            {chargesCalculator?.map((charge: any, index: number) => (
                                <Grid item key={index} xs={12} sm={6} md={3}>
                                    <Charge
                                        type='charge'
                                        setCalculatorData={setChargesCalculator}
                                        charge={charge}
                                        isInCalculator
                                        setShowNotification={setShowNotification}
                                        setNotificationText={setNotificationText}
                                    />
                                </Grid>
                            ))}

                            {violationCalculator?.map((charge: any, index: number) => (
                                <Grid item key={index} xs={12} sm={6} md={3}>
                                    <Charge
                                        type='violation'
                                        setCalculatorData={setViolationCalculator}
                                        charge={charge}
                                        isInCalculator
                                        setShowNotification={setShowNotification}
                                        setNotificationText={setNotificationText}
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                </Box>

                {/* FOOTER */}
                <Footer />
            </Box>
        </>
    )
}
